import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  type SortingStrategy,
} from "@dnd-kit/sortable"

import { cn } from "@/utils/cn"

import type { DashboardWidget } from "../../types/core"
import {
  getDashboardBreakpoint,
  packDashboardRows,
  sortWidgetsByFlowOrder,
} from "../../utils/dashboardFlowLayout"
import {
  getDropSlots,
  getRowBoundarySlots,
  setWidgetWidth,
  type WidgetMoveTarget,
} from "../../utils/dashboardFlowMoves"
import {
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GUTTER_PX,
  getWidgetHeightPx,
} from "../../utils/dashboardWidgetSizing"
import { WidgetDragHandleProvider } from "./WidgetDragHandleContext"
import {
  dropSlotId,
  isDropSlotId,
  RowBoundaryDropSlot,
  rowBoundarySlotId,
  WidgetDropSlot,
} from "./WidgetDropSlot"
import { WidgetResizeHandle } from "./WidgetResizeHandle"

/** Half a gutter of padding per cell adds up to one gutter between widgets. */
const CELL_PADDING_PX = DASHBOARD_GUTTER_PX / 2

/**
 * Space between the outermost cards and the edge of the scroll area, the same
 * on all four sides. Each cell already carries half a gutter of its own, so the
 * container only lays down the remainder.
 */
const GRID_EDGE_PADDING_PX = 24

/** The space a dragged card came from, held open as an empty outline. */
const DRAG_SOURCE_CLASSES =
  "rounded-lg border border-dashed border-coval-border-active"

/**
 * Only a press that lands on a widget's drag handle starts a drag, so the rest
 * of the card stays clickable. A control inside the handle keeps its press too,
 * unless the handle marks it `data-widget-drag-through` — the title link does,
 * so it clicks when tapped and drags when pulled; the activation constraint
 * tells the two apart.
 */
function isDragHandlePress(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false
  }
  const control = target.closest(
    "button, a, input, textarea, select, [role='menu']",
  )
  if (control && !control.closest("[data-widget-drag-through]")) {
    return false
  }
  return Boolean(target.closest(".widget-drag-handle"))
}

/** A mouse or pen drag: a short pull past the activation distance. */
class WidgetDragHandleSensor extends PointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: ({ nativeEvent }: React.PointerEvent) =>
        // A finger is left to the touch sensor, which knows a drag from a
        // scroll by how long it is held; a pull would fail that test.
        nativeEvent.pointerType !== "touch" &&
        isDragHandlePress(nativeEvent.target),
    },
  ]
}

/**
 * A touch drag: press and hold, then move. The handle is a whole title bar, or
 * a whole text card, so a finger landing on it and moving straight away must
 * scroll the dashboard; only a held finger picks the card up. Once it has,
 * dnd-kit cancels the browser's touch moves, so the drag runs in any direction
 * without the page scrolling under it.
 */
class WidgetTouchDragHandleSensor extends TouchSensor {
  static activators = [
    {
      eventName: "onTouchStart" as const,
      handler: ({ nativeEvent }: React.TouchEvent) =>
        nativeEvent.touches.length === 1 &&
        isDragHandlePress(nativeEvent.target),
    },
  ]
}

/** How long a finger holds still on a handle before it picks the card up. */
const TOUCH_DRAG_HOLD_MS = 250

/** How far a held finger may drift during the hold before it reads as a scroll. */
const TOUCH_DRAG_HOLD_TOLERANCE_PX = 8

/**
 * Cards hold their places while a drag is in flight: only the bar at the gap or
 * boundary under the pointer moves, so every landing point stays where the eye
 * last saw it.
 *
 * This also keeps the cards undistorted. A sorting strategy answers with a scale
 * as well as an offset, sized from the slot a card is heading for — which on a
 * grid of mixed widths is rarely the size the card already is, so cards stretch
 * as they shuffle.
 */
const noSortingDisplacement: SortingStrategy = () => null

/**
 * Three tiers, narrowest intent first: an insertion slot the pointer is inside,
 * then a card the pointer is inside, then the nearest card to the one in flight.
 */
const collisionDetection: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args)

  const slotCollisions = underPointer.filter((collision) =>
    isDropSlotId(String(collision.id)),
  )
  if (slotCollisions.length > 0) {
    return slotCollisions
  }

  // The pointer decides, not the dragged card's own rectangle: a card grabbed
  // near one end of its title bar trails most of its width to the other side
  // of the cursor, so its centre can sit over a card the pointer is nowhere
  // near.
  const cardCollisions = underPointer.filter(
    (collision) => !isDropSlotId(String(collision.id)),
  )
  if (cardCollisions.length > 0) {
    return cardCollisions
  }

  // Only once the pointer has left every card does its rectangle get a say, and
  // then only over cards: slots are thin strips laid along the gutters, so their
  // centres crowd together and would win this from well outside themselves.
  return closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (container) => !isDropSlotId(String(container.id)),
    ),
  })
}

interface FlowGridCellProps {
  widget: DashboardWidget
  columns: number
  /** Explicit grid placement, so slots can share the grid without shifting it. */
  rowIndex: number
  columnOffset: number
  /** Omitted when the container is too narrow for widths to differ. */
  resize?: {
    columnWidthPx: number
    onPreview: (widgetId: string, columns: number) => void
    onCommit: (widgetId: string, columns: number) => void
    onCancel: () => void
  }
  children: React.ReactNode
}

const FlowGridCell: React.FC<FlowGridCellProps> = ({
  widget,
  columns,
  rowIndex,
  columnOffset,
  resize,
  children,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useSortable({
      id: widget.id,
      // Left on, a drop slides every card that changed place from where it was
      // to where it now is. The row has already made room by the time the card
      // lands, so the sliding only replays a rearrangement that has happened.
      animateLayoutChanges: () => false,
    })

  const dragHandle = useMemo(
    () => ({ attributes, listeners, setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef],
  )

  return (
    <div
      ref={setNodeRef}
      data-covi-highlight-id={widget.id}
      className="group/widget-cell relative box-border flex min-w-0 flex-col"
      style={{
        gridRow: rowIndex + 1,
        gridColumn: `${columnOffset + 1} / span ${columns}`,
        minHeight: getWidgetHeightPx(widget) + DASHBOARD_GUTTER_PX,
        padding: CELL_PADDING_PX,
      }}
    >
      {/*
       * While the card itself rides the cursor in the drag overlay, the space it
       * came from holds an outline of its shape. The card fades out rather than
       * hiding, so a keyboard drag keeps the focus its arrow keys depend on.
       */}
      <div className={cn("min-h-0 min-w-0 flex-1", isDragging && "opacity-0")}>
        <WidgetDragHandleProvider value={dragHandle}>
          {children}
        </WidgetDragHandleProvider>
      </div>

      {isDragging && (
        <div
          aria-hidden="true"
          data-testid={`dashboard-widget-${widget.id}-drag-placeholder`}
          className={cn("pointer-events-none absolute", DRAG_SOURCE_CLASSES)}
          style={{ inset: CELL_PADDING_PX }}
        />
      )}

      {resize && !isDragging && (
        <WidgetResizeHandle
          widget={widget}
          columnWidthPx={resize.columnWidthPx}
          onPreview={(nextColumns) => resize.onPreview(widget.id, nextColumns)}
          onCommit={(nextColumns) => resize.onCommit(widget.id, nextColumns)}
          onCancel={resize.onCancel}
        />
      )}
    </div>
  )
}

interface FlowDashboardGridProps {
  widgets: DashboardWidget[]
  renderWidget: (widget: DashboardWidget) => React.ReactNode
  onMoveWidget?: (activeWidgetId: string, target: WidgetMoveTarget) => void
  onWidthChange?: (widgetId: string, columns: number) => void
}

/**
 * Renders the dashboard as a sequence of full-width rows on a fixed column
 * grid. Widths come from each widget's own setting, narrowed to what the
 * container can fit; heights come from the widget type. Dragging reorders the
 * sequence — it never places a widget at an arbitrary spot.
 */
export const FlowDashboardGrid: React.FC<FlowDashboardGridProps> = ({
  widgets,
  renderWidget,
  onMoveWidget,
  onWidthChange,
}) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [draggingWidgetId, setDraggingWidgetId] = useState<string | null>(null)
  const [resizePreview, setResizePreview] = useState<{
    widgetId: string
    columns: number
  } | null>(null)

  useEffect(() => {
    const element = gridRef.current
    if (!element) {
      return
    }

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width)
    }

    updateWidth()
    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(element)
    return () => resizeObserver.disconnect()
  }, [])

  const sensors = useSensors(
    useSensor(WidgetDragHandleSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(WidgetTouchDragHandleSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_HOLD_MS,
        tolerance: TOUCH_DRAG_HOLD_TOLERANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const orderedWidgets = useMemo(
    () => sortWidgetsByFlowOrder(widgets),
    [widgets],
  )

  // Before the first measurement the container width is 0, which would pack
  // every widget full width and then reflow. Fall back to the widest layout.
  const breakpoint =
    containerWidth > 0 ? getDashboardBreakpoint(containerWidth) : "wide"

  // Widths only differ from one another on a wide container, so that is the
  // only place a resize handle has anything to show.
  const isResizable = Boolean(onWidthChange) && breakpoint === "wide"

  const rows = useMemo(() => {
    // Previewing through the same call the drop makes keeps the row mid-resize
    // identical to the row that is saved, neighbours and all.
    const previewed = resizePreview
      ? setWidgetWidth(
          orderedWidgets,
          resizePreview.widgetId,
          resizePreview.columns,
        )
      : orderedWidgets

    return packDashboardRows(previewed, breakpoint)
  }, [orderedWidgets, breakpoint, resizePreview])

  const resize = useMemo(
    () =>
      isResizable
        ? {
            columnWidthPx: containerWidth / DASHBOARD_GRID_COLUMNS,
            onPreview: (widgetId: string, columns: number) =>
              setResizePreview({ widgetId, columns }),
            onCommit: (widgetId: string, columns: number) => {
              setResizePreview(null)
              onWidthChange?.(widgetId, columns)
            },
            onCancel: () => setResizePreview(null),
          }
        : undefined,
    [isResizable, containerWidth, onWidthChange],
  )

  const sortableIds = useMemo(
    () => orderedWidgets.map((widget) => widget.id),
    [orderedWidgets],
  )

  const draggingWidget = useMemo(
    () =>
      draggingWidgetId
        ? (orderedWidgets.find((widget) => widget.id === draggingWidgetId) ??
          null)
        : null,
    [orderedWidgets, draggingWidgetId],
  )

  // Slots describe rows as they are stored, so they only make sense on a wide
  // container, where the rows on screen are those same rows.
  const showsDropSlots = Boolean(draggingWidgetId) && breakpoint === "wide"

  const dropSlots = useMemo(
    () =>
      showsDropSlots && draggingWidgetId
        ? getDropSlots(rows, draggingWidgetId)
        : [],
    [rows, draggingWidgetId, showsDropSlots],
  )

  const rowBoundarySlots = useMemo(
    () =>
      showsDropSlots && draggingWidgetId
        ? getRowBoundarySlots(rows, draggingWidgetId)
        : [],
    [rows, draggingWidgetId, showsDropSlots],
  )

  /** What dropping on `candidateId` would mean, or null if it would mean nothing. */
  const resolveMoveTarget = useCallback(
    (candidateId: string | null): WidgetMoveTarget | null => {
      if (!candidateId || candidateId === draggingWidgetId) {
        return null
      }

      const boundary = rowBoundarySlots.find(
        (candidate) =>
          rowBoundarySlotId(candidate.boundaryIndex) === candidateId,
      )
      if (boundary) {
        return {
          kind: "newRow",
          ...(boundary.beforeWidgetId
            ? { beforeWidgetId: boundary.beforeWidgetId }
            : {}),
        }
      }

      const slot = dropSlots.find(
        (candidate) =>
          dropSlotId(candidate.rowIndex, candidate.slotIndex) === candidateId,
      )
      if (slot) {
        return {
          kind: "slot",
          rowAnchorId: slot.rowAnchorId,
          slotIndex: slot.slotIndex,
        }
      }

      return {
        kind: "widget",
        overWidgetId: candidateId,
        rebalanceRows: breakpoint === "wide",
      }
    },
    [draggingWidgetId, breakpoint, dropSlots, rowBoundarySlots],
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingWidgetId(String(event.active.id))
  }, [])

  const handleDragCancel = useCallback(() => {
    setDraggingWidgetId(null)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingWidgetId(null)

      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }

      const target = resolveMoveTarget(String(over.id))
      if (target) {
        onMoveWidget?.(String(active.id), target)
      }
    },
    [onMoveWidget, resolveMoveTarget],
  )

  return (
    <div
      className="min-h-full w-full bg-surface-contrast"
      style={{ padding: GRID_EDGE_PADDING_PX - CELL_PADDING_PX }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={sortableIds} strategy={noSortingDisplacement}>
          <div
            ref={gridRef}
            className="dashboard-flow-grid grid w-full"
            style={{
              gridTemplateColumns: `repeat(${DASHBOARD_GRID_COLUMNS}, minmax(0, 1fr))`,
            }}
          >
            {rows.map((row, rowIndex) => {
              let columnOffset = 0

              return row.widgets.map(({ widget, columns }) => {
                const cellColumnOffset = columnOffset
                columnOffset += columns

                return (
                  <FlowGridCell
                    key={widget.id}
                    widget={widget}
                    columns={columns}
                    rowIndex={rowIndex}
                    columnOffset={cellColumnOffset}
                    resize={resize}
                  >
                    {renderWidget(widget)}
                  </FlowGridCell>
                )
              })
            })}

            {dropSlots.map((slot) => (
              <WidgetDropSlot
                key={dropSlotId(slot.rowIndex, slot.slotIndex)}
                id={dropSlotId(slot.rowIndex, slot.slotIndex)}
                rowIndex={slot.rowIndex}
                columnOffset={slot.columnOffset}
              />
            ))}

            {rowBoundarySlots.map((slot) => (
              <RowBoundaryDropSlot
                key={rowBoundarySlotId(slot.boundaryIndex)}
                id={rowBoundarySlotId(slot.boundaryIndex)}
                boundaryIndex={slot.boundaryIndex}
                rowCount={rows.length}
              />
            ))}
          </div>
        </SortableContext>

        {/*
         * The overlay's copy of the widget sits outside the drag handle provider
         * and so renders inert. Dropping removes it in the same frame the grid
         * reflows, which avoids animating a second card over the reordered one.
         */}
        <DragOverlay dropAnimation={null}>
          {draggingWidget && (
            <div
              data-testid="dashboard-widget-drag-overlay"
              // The shadow lands on the card rather than this padded wrapper, so
              // it follows the card's own rounded edge.
              className="pointer-events-none box-border h-full w-full cursor-grabbing [&>*]:shadow-lg"
              style={{
                padding: CELL_PADDING_PX,
              }}
            >
              {renderWidget(draggingWidget)}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
